import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  marketingErrorKey,
  useCreateEmailTemplateMutation,
  useEmailTemplateQuery,
  useMarketingLimits,
  useUpdateEmailTemplateMutation,
} from "@/partner/entities/marketing";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { DialogHeader } from "@/shared/components";
import { useUnsavedChangesGuard } from "@/partner/shared/hooks";
import { EMAIL_TEMPLATE_LIMITS, toMarketingLanguage } from "../model/blocks";
import {
  activeVariant,
  canSave,
  editorReducer,
  emptyEditorState,
  fromEmailTemplate,
  toEmailTemplateInput,
  type EditorState,
} from "../model/editorState";
import { AddBlockMenu } from "./AddBlockMenu";
import { BlockList } from "./BlockList";
import { EmailPreviewFrame } from "./EmailPreviewFrame";
import { TestSendButton } from "./TestSendButton";
import { VariantTabs } from "./VariantTabs";

// Переменная в подсказке передаётся параметром, см. BlockEditor.
const FIRST_NAME_VARIABLE = "{{firstName}}";

const serialize = (state: EditorState) =>
  JSON.stringify(toEmailTemplateInput(state));

interface EditorFormProps {
  templateId: string | null;
  initialState: EditorState;
  onClose: () => void;
  onCreated: (id: string, state: EditorState) => void;
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Шапка и тело диалога: блоки слева, живое превью справа (как редактор
 * обложек CustomCoverEditor). «Отправить тест себе» и «Сохранить» в шапке.
 */
const EditorForm: FC<EditorFormProps> = ({
  templateId,
  initialState,
  onClose,
  onCreated,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(editorReducer, initialState);
  // Снимок последнего сохранённого состояния: «грязно», если вход мутации
  // отличается от него (переключение вкладки языка правкой не считается).
  const [saved, setSaved] = useState(() => serialize(initialState));
  const dirty = useMemo(() => serialize(state) !== saved, [state, saved]);
  useUnsavedChangesGuard(dirty, t("shared.form.unsavedChangesWarning"));
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const variant = activeVariant(state);
  // Переменные-адреса кнопок (письма цепочек) приходят с сервера; пока
  // запрос не ответил, кабинет их не принимает, сохранение проверит бэкенд.
  const limits = useMarketingLimits();
  const urlVariables = useMemo(() => limits?.emailUrlVariables ?? [], [limits]);
  // Как в остальных диалогах: пустое обязательное поле краснеет только
  // после того, как с него ушли, а не сразу при открытии.
  const [touched, setTouched] = useState({ name: false, subject: false });
  const onError = (error: MutationErrorLike) =>
    enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" });

  const [createTemplate, { loading: creating }] =
    useCreateEmailTemplateMutation({ onError });
  const [updateTemplate, { loading: updating }] =
    useUpdateEmailTemplateMutation({ onError });
  const saving = creating || updating;

  const onSave = async () => {
    const input = toEmailTemplateInput(state);
    const snapshot = serialize(state);
    if (templateId) {
      const result = await updateTemplate({
        variables: { id: templateId, input },
      });
      if (result.data) {
        setSaved(snapshot);
        enqueueSnackbar(t("emailTemplates.editor.saved"), {
          variant: "success",
        });
      }
      return;
    }
    const result = await createTemplate({ variables: { input } });
    const id = result.data?.createEmailTemplate.id;
    if (id) {
      setSaved(snapshot);
      // Правок больше нет: смена адреса на ?tab=edit&id= не спросит
      // подтверждения.
      onDirtyChange(false);
      enqueueSnackbar(t("emailTemplates.editor.created"), {
        variant: "success",
      });
      onCreated(id, state);
    }
  };

  return (
    <>
      <DialogHeader
        title={
          templateId
            ? t("emailTemplates.editor.title")
            : t("emailTemplates.editor.newTitle")
        }
        loading={saving}
        onClose={onClose}
        actions={[
          <TestSendButton
            key="test"
            templateId={templateId}
            languages={state.variants.map((v) => v.language)}
            disabled={dirty}
          />,
          <Button
            key="save"
            variant="contained"
            disableElevation
            onClick={onSave}
            disabled={saving || !dirty || !canSave(state, urlVariables)}
          >
            {t("emailTemplates.editor.save")}
          </Button>,
        ]}
      />
      <DialogContent sx={{ p: 1 }}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={0}>
              <CardContent>
                <Stack spacing={2}>
                  <TextField
                    label={t("emailTemplates.editor.name")}
                    value={state.name}
                    onChange={(event) =>
                      dispatch({ type: "setName", name: event.target.value })
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, name: true }))
                    }
                    error={touched.name && !state.name.trim()}
                    helperText={t("emailTemplates.editor.nameHint")}
                    slotProps={{
                      htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.name },
                    }}
                    fullWidth
                  />
                  <VariantTabs
                    languages={state.variants.map((v) => v.language)}
                    active={variant.language}
                    onSelect={(language) =>
                      dispatch({ type: "setActiveLanguage", language })
                    }
                    onAdd={(language) =>
                      dispatch({ type: "addVariant", language })
                    }
                    onRemove={(language) =>
                      dispatch({ type: "removeVariant", language })
                    }
                  />
                  <TextField
                    label={t("emailTemplates.editor.subject")}
                    value={variant.subject}
                    onChange={(event) =>
                      dispatch({
                        type: "setSubject",
                        subject: event.target.value,
                      })
                    }
                    onBlur={() =>
                      setTouched((prev) => ({ ...prev, subject: true }))
                    }
                    error={touched.subject && !variant.subject.trim()}
                    helperText={t("emailTemplates.editor.subjectHint", {
                      variable: FIRST_NAME_VARIABLE,
                    })}
                    slotProps={{
                      htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.subject },
                    }}
                    fullWidth
                  />
                  <TextField
                    label={t("emailTemplates.editor.preheader")}
                    value={variant.preheader}
                    onChange={(event) =>
                      dispatch({
                        type: "setPreheader",
                        preheader: event.target.value,
                      })
                    }
                    helperText={t("emailTemplates.editor.preheaderHint")}
                    slotProps={{
                      htmlInput: {
                        maxLength: EMAIL_TEMPLATE_LIMITS.preheader,
                      },
                    }}
                    fullWidth
                  />
                  <BlockList
                    key={variant.language}
                    blocks={variant.blocks}
                    urlVariables={urlVariables}
                    onMove={(from, to) =>
                      dispatch({ type: "moveBlock", from, to })
                    }
                    onUpdate={(id, patch) =>
                      dispatch({ type: "updateBlock", id, patch })
                    }
                    onDuplicate={(id) =>
                      dispatch({ type: "duplicateBlock", id })
                    }
                    onRemove={(id) => dispatch({ type: "removeBlock", id })}
                  />
                  <AddBlockMenu
                    blocksCount={variant.blocks.length}
                    onAdd={(blockType) =>
                      dispatch({ type: "addBlock", blockType })
                    }
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ position: "sticky", top: 0 }}>
            <Card elevation={0}>
              <CardContent>
                {/* key по языку: иначе до конца задержки превью показывало
                    бы письмо предыдущей вкладки */}
                <EmailPreviewFrame
                  key={variant.language}
                  variant={variant}
                  urlVariables={urlVariables}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  );
};

interface EmailTemplateDialogProps {
  /** id письма; null открывает создание нового. */
  templateId: string | null;
  onClose: () => void;
  /** Письмо создано: список переключает адрес на ?tab=edit&id=. */
  onCreated: (id: string) => void;
}

/**
 * Диалог письма на странице списка (?tab=create, ?tab=edit&id=), fullScreen
 * с DialogHeader, как CustomCoverEditor. Новое письмо открывается на языке
 * платформы (если он среди языков писем, иначе en). Форма монтируется только
 * с готовым начальным состоянием, чтобы useReducer не пришлось пересобирать
 * после ответа сервера. Закрытие с несохранёнными правками спрашивает
 * подтверждение, как EditVideoDialog при активной загрузке.
 */
export const EmailTemplateDialog: FC<EmailTemplateDialogProps> = ({
  templateId,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const isNew = templateId === null;
  const platform = useCurrentPlatformQuery({ skip: !isNew });
  const template = useEmailTemplateQuery({
    variables: { id: String(templateId) },
    skip: isNew,
    fetchPolicy: "network-only",
  });
  const dirtyRef = useRef(false);
  // Стабильная ссылка: иначе эффект формы вызывался бы на каждый рендер.
  const onDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const handleClose = () => {
    if (
      dirtyRef.current &&
      !window.confirm(t("shared.form.unsavedChangesWarning"))
    ) {
      return;
    }
    onClose();
  };

  const initialState = useMemo(() => {
    if (isNew) {
      if (platform.loading && !platform.data) return null;
      return emptyEditorState(
        toMarketingLanguage(platform.data?.currentPlatform?.language?.code)
      );
    }
    return template.data
      ? fromEmailTemplate(template.data.emailTemplate)
      : null;
  }, [isNew, platform.loading, platform.data, template.data]);

  // Первое загруженное состояние запоминается для этого id: если refetch
  // после сохранения упадёт и data пропадёт, форма с правками не должна
  // смениться спиннером или ошибкой загрузки.
  const [loaded, setLoaded] = useState<{
    templateId: string | null;
    state: EditorState;
  } | null>(null);
  if (initialState && (!loaded || loaded.templateId !== templateId)) {
    setLoaded({ templateId, state: initialState });
  }
  const formState =
    loaded && loaded.templateId === templateId ? loaded.state : initialState;

  // Созданное письмо открывается в том же состоянии формы сразу, без
  // спиннера на время загрузки по новому id.
  const handleCreated = (id: string, state: EditorState) => {
    setLoaded({ templateId: id, state });
    onCreated(id);
  };

  // Ошибку загрузки показываем, только пока данных нет.
  const loadFailed =
    !isNew && Boolean(template.error) && !template.data && !formState;

  return (
    <Dialog open onClose={handleClose} fullScreen disableEscapeKeyDown>
      {formState ? (
        <EditorForm
          key={templateId ?? "new"}
          templateId={templateId}
          initialState={formState}
          onClose={handleClose}
          onCreated={handleCreated}
          onDirtyChange={onDirtyChange}
        />
      ) : (
        <>
          <DialogHeader
            title={
              isNew
                ? t("emailTemplates.editor.newTitle")
                : t("emailTemplates.editor.title")
            }
            loading={!loadFailed}
            onClose={handleClose}
          />
          <DialogContent sx={{ p: 1 }}>
            {loadFailed ? (
              <Alert severity="error">
                {t("emailTemplates.editor.loadFailed")}
              </Alert>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
